import Card from "components/card";
import React, { useState, useEffect, useMemo } from "react";
import { IoPencil, IoTrash, IoAdd, IoCheckmark, IoClose } from "react-icons/io5";
import { addLeadNote, deleteLeadNote, updateLeadNote } from "../../tables/leadApi";

const ClientNotes = ({ client }) => {
  // Extract notes array from client if available (memoized to stabilize useEffect deps)
  const notesArray = useMemo(() => (Array.isArray(client?.notes) ? client.notes : []), [client?.notes]);
  // Layout settings: show VISIBLE_ITEMS before scroll and render placeholders to fill the card
  const ITEM_HEIGHT_PX = 64; // approx h-16
  const VISIBLE_ITEMS = 6; // show 6 items before scrolling
  const GRID_GAP_PX = 12; // gap between items
  const GRID_PADDING_PX = 16; // top+bottom padding buffer
  const computedMaxHeight = VISIBLE_ITEMS * ITEM_HEIGHT_PX + (VISIBLE_ITEMS - 1) * GRID_GAP_PX + GRID_PADDING_PX;
  
  function normalizeNotes(rawNotes = []) {
    return Array.isArray(rawNotes)
      ? rawNotes
          .filter((item) => item && (item.note || item.content))
          .map((item) => ({
            _id: item._id || item.id,
            note: item.note || item.content,
            date: item.date || item.createdAt || new Date().toISOString(),
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      : [];
  }

  const [notes, setNotes] = useState(() => normalizeNotes(notesArray));
  const [newNote, setNewNote] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const MAX_NOTE_LENGTH = 1000;
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  useEffect(() => {
    setNotes(normalizeNotes(notesArray));
  }, [notesArray]);

  const clientId = client?._id || client?.id;

  const handleNewNoteChange = (e) => {
    const val = String(e.target.value || "").slice(0, MAX_NOTE_LENGTH);
    setNewNote(val);
  };

  const handleNewNotePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text") || "";
    e.preventDefault();
    const el = e.target;
    const { selectionStart = 0, selectionEnd = 0, value = "" } = el;
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const allowed = Math.max(0, MAX_NOTE_LENGTH - (before.length + after.length));
    const toInsert = paste.slice(0, allowed);
    const newVal = (before + toInsert + after).slice(0, MAX_NOTE_LENGTH);
    setNewNote(newVal);
    const caretPos = before.length + toInsert.length;
    setTimeout(() => {
      try { el.setSelectionRange(caretPos, caretPos); } catch (err) {}
    }, 0);
  };

  const handleEditingTextChange = (e) => {
    const val = String(e.target.value || "").slice(0, MAX_NOTE_LENGTH);
    setEditingText(val);
  };

  const handleEditingTextPaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text") || "";
    e.preventDefault();
    const el = e.target;
    const { selectionStart = 0, selectionEnd = 0, value = "" } = el;
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const allowed = Math.max(0, MAX_NOTE_LENGTH - (before.length + after.length));
    const toInsert = paste.slice(0, allowed);
    const newVal = (before + toInsert + after).slice(0, MAX_NOTE_LENGTH);
    setEditingText(newVal);
    const caretPos = before.length + toInsert.length;
    setTimeout(() => {
      try { el.setSelectionRange(caretPos, caretPos); } catch (err) {}
    }, 0);
  };

  const handleAddNote = async () => {
    const trimmed = (newNote || "").trim().slice(0, MAX_NOTE_LENGTH);
    if (!trimmed) return;
    if (!clientId) {
      setError("Unable to identify client.");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const responseData = await addLeadNote({ leadId: clientId, note: trimmed });
      const normalizedNotes = normalizeNotes(responseData?.data?.notes || responseData?.notes);
      setNotes(normalizedNotes.length ? normalizedNotes : [{ _id: `note-${Date.now()}`, note: trimmed, date: new Date().toISOString() }]);
      setNewNote("");
      setShowAddForm(false);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add note.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setError("");
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    const noteId = noteToDelete._id || noteToDelete.id;
    if (!noteId) return;
    if (!clientId) {
      setError("Unable to identify client.");
      setNoteToDelete(null);
      return;
    }

    setIsDeleting(true);
    setError("");

    try {
      await deleteLeadNote(clientId, noteId);
      setNotes((prevNotes) => prevNotes.filter((n) => (n._id || n.id) !== noteId));
      setNoteToDelete(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete note.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setNoteToDelete(null);
    setError("");
  };

  const startEdit = (note) => {
    const noteId = note._id || note.id;
    setEditingId(noteId);
    setEditingText(String(note.note || "").slice(0, MAX_NOTE_LENGTH));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const handleEditNote = async (id) => {
    const trimmed = (editingText || "").trim().slice(0, MAX_NOTE_LENGTH);
    if (!trimmed) return;
    if (!clientId) {
      setError("Unable to identify client.");
      return;
    }

    setIsEditSaving(true);
    setError("");

    try {
      await updateLeadNote(clientId, id, { note: trimmed });
      setNotes((prevNotes) =>
        prevNotes.map((n) =>
          (n._id || n.id) === id ? { ...n, note: trimmed } : n
        )
      );
      setEditingId(null);
      setEditingText("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update note.");
    } finally {
      setIsEditSaving(false);
    }
  };

  return (
    <Card extra="w-full rounded-[24px] border border-gray-200 p-5 shadow-sm dark:border-white/10 dark:bg-navy-700 flex flex-col h-full">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-navy-900 dark:text-white">Notes</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Background & next steps</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-600"
        >
          <IoAdd className="text-sm" />
          Add
        </button>
      </div>

      {/* Add Note Form */}
      {showAddForm && (
        <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-3 dark:border-white/10 dark:bg-navy-800">
          <textarea
            value={newNote}
            onChange={handleNewNoteChange}
            onPaste={handleNewNotePaste}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="Type your note here..."
            className="w-full rounded-xl border border-gray-200 bg-white p-2 text-xs text-navy-900 outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-gray-300"
            rows="3"
          />
          <div className="mt-1 flex justify-end text-xs text-gray-500 dark:text-gray-400">{(newNote || "").length}/{MAX_NOTE_LENGTH}</div>
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={handleAddNote}
              disabled={isSaving}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-50"
            >
              <IoCheckmark className="text-sm" />
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewNote("");
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-400 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-500"
            >
              <IoClose className="text-sm" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1" style={{ maxHeight: `${computedMaxHeight}px` }}>
        {error && (
          <div className="rounded-xl bg-red-50 p-3 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-200">
            {error}
          </div>
        )}
        {notes.length === 0 ? (
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 py-4">No notes yet</p>
        ) : (
          <>
            {notes.map((note) => {
              const noteId = note._id || note.id;
              return (
                <div key={noteId} className="rounded-xl border border-gray-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-navy-800">
                  {editingId === noteId ? (
                    <div>
                      <textarea
                        value={editingText}
                        onChange={handleEditingTextChange}
                        onPaste={handleEditingTextPaste}
                        maxLength={MAX_NOTE_LENGTH}
                        className="w-full rounded-lg border border-gray-200 bg-white p-2 text-xs text-navy-900 outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-gray-300"
                        rows="3"
                      />
                      <div className="mt-1 flex justify-end text-xs text-gray-500 dark:text-gray-400">{(editingText || "").length}/{MAX_NOTE_LENGTH}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={() => handleEditNote(note._id || note.id)}
                          disabled={isEditSaving}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-2 py-1 text-xs font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-50"
                        >
                          <IoCheckmark className="text-sm" />
                          {isEditSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-400 px-2 py-1 text-xs font-semibold text-white transition hover:bg-slate-500"
                        >
                          <IoClose className="text-sm" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between">
                        <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                          {new Date(note.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(note)}
                            className="rounded-lg bg-blue-500/20 p-1 text-blue-600 transition hover:bg-blue-500/30 dark:text-blue-400"
                            title="Edit note"
                          >
                            <IoPencil className="text-xs" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(note)}
                            className="rounded-lg bg-red-500/20 p-1 text-red-600 transition hover:bg-red-500/30 dark:text-red-400"
                            title="Delete note"
                          >
                            <IoTrash className="text-xs" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-navy-700 dark:text-gray-300 whitespace-pre-wrap break-all">{note.note}</p>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Render invisible placeholders to visually fill up to VISIBLE_ITEMS */}
            {Array.from({ length: Math.max(0, VISIBLE_ITEMS - notes.length) }).map((_, i) => (
              <div
                key={`placeholder-${i}`}
                className="rounded-xl border border-transparent bg-transparent p-3 h-16"
                aria-hidden
              />
            ))}
          </>
        )}
      </div>

      {noteToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <IoTrash className="text-xl text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-navy-700 dark:text-white">Delete Note</h2>
            </div>

            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this note? This action cannot be undone.
            </p>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-200">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${isDeleting ? "bg-red-300 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"}`}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ClientNotes;
